import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys } from "@/lib/api/dashboard";
import { useGuildRoute } from "@/hooks/use-guild-route";
import type {
  SubscriptionResponse,
  LockedGuildsResponse,
  GuildLockStatusResponse,
  PortalSessionResponse,
  CheckoutStatusResponse,
} from "@/lib/api/types";

export const subscriptionKeys = {
  all: ["subscription"] as const,
  detail: (tenantId: string) => [...subscriptionKeys.all, tenantId] as const,
};

export const subscriptionQueryOptions = (
  tenantId: string,
  applicationGuildId?: string
) =>
  queryOptions({
    queryKey: [
      ...subscriptionKeys.detail(tenantId),
      applicationGuildId ?? null,
    ],
    queryFn: () => {
      const params = applicationGuildId
        ? `?applicationGuildId=${applicationGuildId}`
        : "";
      return apiClient
        .get<SubscriptionResponse>(
          `/api/tenants/${tenantId}/subscription${params}`
        )
        .then((r) => r.data);
    },
    enabled: !!tenantId,
  });

export function useSubscription(tenantId: string, applicationGuildId?: string) {
  return useQuery({
    ...subscriptionQueryOptions(tenantId, applicationGuildId),
    placeholderData: keepPreviousData,
  });
}

export function useCreatePortalSession(tenantId: string) {
  return useMutation({
    mutationFn: () =>
      apiClient
        .post<PortalSessionResponse>(
          `/api/tenants/${tenantId}/subscription/portal`
        )
        .then((r) => r.data),
    onSuccess: (data) => {
      window.location.href = data.data.url;
    },
  });
}

export function useCheckoutStatus(tenantId: string, sessionId: string | null) {
  return useQuery({
    queryKey: [
      ...subscriptionKeys.detail(tenantId),
      "checkout-status",
      sessionId,
    ],
    queryFn: () =>
      apiClient
        .get<CheckoutStatusResponse>(
          `/api/tenants/${tenantId}/subscription/checkout-status?session_id=${sessionId}`
        )
        .then((r) => r.data),
    enabled: !!tenantId && !!sessionId,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      if (status === "complete" || status === "expired") return false;
      return 2000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useGuildSubscription() {
  const { tenantId, applicationGuildId } = useGuildRoute();
  const subscription = useSubscription(
    tenantId!,
    applicationGuildId ?? undefined
  );
  return { ...subscription, tenantId };
}

const lockedGuildsQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: [...subscriptionKeys.detail(tenantId), "locked"],
    queryFn: () =>
      apiClient
        .get<LockedGuildsResponse>(
          `/api/tenants/${tenantId}/subscription/locked-guilds`
        )
        .then((r) => r.data),
    enabled: !!tenantId,
  });

export function useLockedGuilds(tenantId: string) {
  return useQuery(lockedGuildsQueryOptions(tenantId));
}

export function useUnlockGuild(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationGuildId: string) =>
      apiClient
        .post<GuildLockStatusResponse>(
          `/api/tenants/${tenantId}/subscription/unlock/${applicationGuildId}`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.detail(tenantId),
      });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}
